import React, { useEffect, useState } from 'react';
import { Col, Row } from '@amzn/stencil-react-components/layout';
import { Modal } from '@amzn/stencil-react-components/modal';
import { H2, Text } from '@amzn/stencil-react-components/text';
import { useDispatch, useSelector } from 'react-redux';
import {
  createContentSelector,
  templateContentSelector,
  templateListSelector,
} from '../../selectors/selectors';
import BorderedView from '../common/BorderedView';
import { Input, InputWrapper } from '@amzn/stencil-react-components/input';
import {
  saveMessageContent,
  updateMessageTemplate,
  updateTemplateName,
} from '../../actions/nudgeActions';
import { Button } from '@amzn/stencil-react-components/button';
import ButtonWithLoader from '../atoms/ButtonWithLoader';
import { withTooltip } from '@amzn/stencil-react-components/tooltip';
import { useUserInfo } from '../../hooks/useUserInfo';
import { FormType } from '../../enums/commonEnums';
import useTemplateFormData from '../../hooks/useTemplateFormData';
import { ResponseStatus } from '../../enums/responseStatus';
import { FormattedMessage, useIntl } from 'react-intl';
import { templateNameValidator } from '../../util/templateBuilderUtil';
import { IconExclamationTriangle } from '../../mocks/stencilV4IconMocks';

const IconWithTooltip = withTooltip()(IconExclamationTriangle);
const TemplateSaveModal = ({ isOpen, close }) => {
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();
  const contentElements = useSelector(templateContentSelector);
  const templateList = useSelector(templateListSelector);
  const userInfo = useUserInfo();
  const { subject, templateName, formMode } = useSelector(createContentSelector);
  const { createStatus, editStatus } = useTemplateFormData();

  const [isTemplateNameValid, setIsTemplateNameValid] = useState(false);
  const [templateMessage, setTemplateMessage] = useState();
  const [messageTemplateName, setMessageTemplateName] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const onTemplateNameChange = event => {
    const { isValidTemplateName, message } = templateNameValidator(event, templateList);
    setIsTemplateNameValid(isValidTemplateName);
    setTemplateMessage(message);
    setMessageTemplateName(event);
    dispatch(updateTemplateName(event));
  };

  const saveButtonClickHandler = async () => {
    await onFormSubmit();
  };

  useEffect(
    () => {
      if (formMode === FormType.EDIT) {
        setMessageTemplateName(templateName);
      }
    },
    [formMode],
  );

  // close the modal after successful api operation
  useEffect(
    () => {
      if (createStatus === ResponseStatus.SUCCESS || editStatus === ResponseStatus.SUCCESS) {
        close();
      }
    },
    [createStatus, editStatus],
  );

  const onFormSubmit = async () => {
    setIsLoading(true);
    const messageHtmlPart = contentElements?.reduce((previousValue, currentContent) => {
      return `${previousValue}<div id='${currentContent.id}' data-type='${currentContent.type}'>${
        currentContent.content
      }</div>`;
    }, '');
    const userName = userInfo?.user;
    const userGroupName = userInfo?.selectedAccessGroupName;
    const messageTemplate = {
      messageTemplateName: templateName,
      subject,
      messageHtmlPart,
      messageTextPart: '',
      createdBy: userName,
    };
    const sessionToken = userInfo?.idToken;

    if (formMode === FormType.EDIT) {
      await dispatch(updateMessageTemplate(userName, messageTemplate, sessionToken));
      setIsLoading(false);
      return;
    }
    await dispatch(saveMessageContent(userName, userGroupName, messageTemplate, sessionToken));
    setIsLoading(false);
  };

  const isEditMode = formMode === FormType.EDIT;

  return (
    <>
      <Modal isOpen={isOpen} close={close}>
        <Col
          minWidth={'50vw'}
          backgroundColor="neutral0"
          gridGap="S500"
          padding="S500"
          alignItems="center"
        >
          <H2>
            <FormattedMessage
              id="app.templateSaveModal.label.saveTemplate"
              defaultMessage="Save Template"
            />
          </H2>

          <Col alignSelf="flex-start" gridGap="S300" flex="1" width="100%">
            <Col gridGap="S100" justifyContent="stretch">
              <Text fontWeight="bold">
                <FormattedMessage
                  id="app.contentPanel.label.subjectTitle"
                  defaultMessage="Subject"
                />
              </Text>
              <BorderedView borderColor="gray" flex={1} style={{ padding: '10px' }}>
                {subject}
              </BorderedView>
            </Col>

            <Col gridGap="S100" justifyContent="stretch">
              <Text fontWeight="bold">
                <FormattedMessage id="app.templateSaveModal.label.body" defaultMessage="Body" />
              </Text>
              <BorderedView borderColor="gray" flex={1} style={{ padding: '10px' }}>
                <Col
                  className="content-styles default"
                  alignSelf="flex-start"
                  dangerouslySetInnerHTML={{
                    __html: contentElements?.reduce((previousValue, currentContent) => {
                      return `${previousValue}<div>${currentContent.content}</div>`;
                    }, ''),
                  }}
                />
              </BorderedView>
            </Col>

            <InputWrapper
              renderLabel={({ children }) => <Text fontWeight="bold">{children}</Text>}
              labelText={formatMessage({
                id: 'app.templateSaveModal.label.templateName',
                defaultMessage: 'Template Name',
              })}
              id="input-id-6"
              footer={templateMessage}
              error={!!templateMessage}
            >
              {inputProps => (
                <Input
                  placeholder={formatMessage({
                    id: 'app.templateSaveModal.label.placeholder',
                    defaultMessage: 'Save template...',
                  })}
                  data-testid="template-name-input"
                  value={messageTemplateName}
                  disabled={isEditMode}
                  onChange={event => onTemplateNameChange(event.target?.value)}
                  {...inputProps}
                />
              )}
            </InputWrapper>
          </Col>

          <Row gridGap={'S200'}>
            <Button onClick={close} secondary>
              <FormattedMessage id="app.createContent.button.cancel" defaultMessage="Cancel" />
            </Button>

            <ButtonWithLoader
              hasIcon
              isDisabled={!isEditMode && (!isTemplateNameValid || isLoading)}
              onClickHandler={saveButtonClickHandler}
              isLoading={isLoading}
              primary
              data-testid="save-btn"
            >
              <Row alignItems="center">
                {isEditMode ? (
                  <FormattedMessage
                    id="app.templateSaveModal.label.update"
                    defaultMessage="Update"
                  />
                ) : (
                  <FormattedMessage id="app.templateSaveModal.label.save" defaultMessage="Save" />
                )}
                {!isEditMode && !isTemplateNameValid ? (
                  <IconWithTooltip
                    tooltipText={formatMessage({
                      id: 'app.templateSaveModal.label.templateError',
                      defaultMessage: 'Please fix template name errors',
                    })}
                  />
                ) : null}
              </Row>
            </ButtonWithLoader>
          </Row>
        </Col>
      </Modal>
    </>
  );
};

export default TemplateSaveModal;
